import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { CheckCircle, House, CalendarBlank, CaretDown, CaretUp, Fire, Envelope } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { BackofficeNotificationBell } from "../components/layout/BackofficeNotificationBell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { api, formatApiErrorDetail } from "../lib/api";
import { ECOMMERCE_MODULE_NAME } from "../constants/brand";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatET } from "../lib/dateUtils";

const navItems = [
  { to: "/backoffice", label: "Weekly Report", icon: <House size={18} weight="duotone" />, testId: "nav-backoffice-form" },
  { to: "/backoffice/trust-intake", label: "Trust Intake", icon: <Envelope size={18} weight="duotone" />, testId: "nav-backoffice-trust-intake" },
];

const STATUS_STYLE = {
  approved: "bg-igc-success/10 text-igc-success",
  rejected: "bg-igc-danger/10 text-igc-danger",
  hold: "bg-igc-danger/10 text-igc-danger",
  pending: "bg-igc-gold/10 text-igc-goldText",
};

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STREAK_MILESTONES = [4, 8];

const initialForm = {
  client_id: "",
  week_ending: "",
  revenue: "",
  orders_placed: "",
  orders_fulfilled: "",
  inventory_status: "Healthy",
  monthly_profit_to_date: "",
  performance_notes: "",
  store_update_text: "",
  projections_notes: "",
};

export default function BackOfficePage() {
  const [stores, setStores] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [reminders, setReminders] = useState({ due_date: "", pending_stores: [], is_overdue: false, streak_weeks: 0 });
  const [fundingStatus, setFundingStatus] = useState(null);

  const fetchMyReports = () => {
    api.get("/backoffice/reports").then(({ data }) => setMyReports(data));
  };

  const fetchReminders = () => {
    api.get("/backoffice/reminders").then(({ data }) => {
      setReminders(data);
      const celebrated = localStorage.getItem("backoffice_celebrated_streak");
      if (STREAK_MILESTONES.includes(data.streak_weeks) && celebrated !== String(data.streak_weeks)) {
        confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#c9a535", "#e5c76b", "#f0ebe3"] });
        localStorage.setItem("backoffice_celebrated_streak", String(data.streak_weeks));
      }
    });
  };

  useEffect(() => {
    api.get("/backoffice/stores").then(({ data }) => setStores(data));
    api.get("/backoffice/funding-status").then(({ data }) => setFundingStatus(data)).catch(() => {});
    fetchMyReports();
    fetchReminders();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.week_ending) {
      setError("Please select a week ending date");
      return;
    }
    try {
      await api.post("/backoffice/reports", {
        ...form,
        revenue: parseFloat(form.revenue),
        orders_placed: parseInt(form.orders_placed, 10),
        orders_fulfilled: parseInt(form.orders_fulfilled, 10),
        monthly_profit_to_date: parseFloat(form.monthly_profit_to_date),
      });
      setSubmitted(true);
      fetchMyReports();
      fetchReminders();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName={ECOMMERCE_MODULE_NAME} topRight={<BackofficeNotificationBell />}>
      <div className="max-w-xl mx-auto" data-testid="backoffice-page">
        {fundingStatus?.has_status && (
          <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-6" data-testid="backoffice-funding-status-card">
            <p className="text-igc-cream font-serif text-lg mb-1">Her Funding Status</p>
            <div className="flex items-center justify-between">
              <span className="text-igc-goldText text-sm">{fundingStatus.stage}</span>
              <span className="text-igc-muted text-xs">Requested ${(fundingStatus.amount_requested || 0).toLocaleString()}</span>
            </div>
            {fundingStatus.amount_funded > 0 && (
              <p className="text-igc-success text-xs mt-1">${fundingStatus.amount_funded.toLocaleString()} funded</p>
            )}
          </div>
        )}
        {reminders.streak_weeks > 0 && (
          <div className="flex items-center gap-2 mb-6 bg-igc-gold/10 border border-igc-gold/30 px-4 py-2.5 w-fit" data-testid="backoffice-streak-badge">
            <Fire size={18} weight="fill" className="text-igc-goldText" />
            <span className="text-igc-goldText text-sm font-medium">
              {reminders.streak_weeks}-week on-time streak
            </span>
          </div>
        )}
        {reminders.pending_stores.length > 0 && (
          <div
            className={`border p-5 mb-8 ${reminders.is_overdue ? "bg-igc-danger/10 border-igc-danger/40" : "bg-igc-gold/10 border-igc-gold/30"}`}
            data-testid="backoffice-pending-reminder"
          >
            <p className={`text-sm font-medium tracking-wide mb-1 ${reminders.is_overdue ? "text-igc-danger" : "text-igc-goldText"}`}>
              {reminders.is_overdue ? "Overdue — Reports Not Submitted" : "Reports Pending Submission"}
            </p>
            <p className={`text-xs mb-3 ${reminders.is_overdue ? "text-igc-danger/80" : "text-igc-muted"}`}>
              {reminders.is_overdue ? `Was due ${reminders.due_date}` : `Due ${reminders.due_date}`}
            </p>
            <div className="flex flex-wrap gap-2">
              {reminders.pending_stores.map((s) => (
                <span
                  key={s.client_id}
                  data-testid={`backoffice-pending-store-${s.client_id}`}
                  className={`text-xs px-3 py-1.5 border ${reminders.is_overdue ? "bg-igc-surface border-igc-danger/30 text-igc-danger" : "bg-igc-surface border-igc-gold/25 text-igc-cream"}`}
                >
                  {s.display_name}
                </span>
              ))}
            </div>
          </div>
        )}
        {submitted ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-24" data-testid="backoffice-confirmation">
            <CheckCircle size={56} weight="fill" className="text-igc-success mx-auto mb-6" />
            <h2 className="font-serif text-2xl text-igc-cream mb-2">Report submitted.</h2>
            <p className="text-igc-muted mb-8">Pending review.</p>
            <Button data-testid="submit-another-button" onClick={() => { setForm(initialForm); setSubmitted(false); }} className="bg-igc-gold hover:bg-igc-goldHover text-black px-8">
              Submit Another Report
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="backoffice-form">
            <h1 className="font-serif text-2xl text-igc-cream mb-6 text-center">Weekly Store Report</h1>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Select Store</label>
              <Select required value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="backoffice-store-select" title="Choose which client store this weekly report is for." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto">
                  <SelectValue placeholder="Choose a store..." />
                </SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  {stores.map((s) => <SelectItem key={s.client_id} value={s.client_id}>{s.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Week Ending Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="backoffice-week-ending-input"
                    title="Pick the last day of the week this report covers."
                    className="w-full flex items-center justify-between bg-igc-surfaceLight border border-igc-gold/20 text-igc-cream text-xl p-6 text-left"
                  >
                    <span className={form.week_ending ? "" : "text-igc-muted"}>
                      {form.week_ending ? format(new Date(form.week_ending), "MMMM d, yyyy") : "Select a date..."}
                    </span>
                    <CalendarBlank size={20} className="text-igc-goldText" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-igc-surfaceLight border-igc-gold/20 w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={form.week_ending ? new Date(form.week_ending) : undefined}
                    onSelect={(d) => d && setForm({ ...form, week_ending: format(d, "yyyy-MM-dd") })}
                    className="text-igc-cream"
                    classNames={{
                      day_selected: "bg-igc-gold text-black hover:bg-igc-gold hover:text-black focus:bg-igc-gold",
                      day_today: "text-igc-goldText",
                      nav_button: "text-igc-cream hover:bg-igc-surface",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Total Revenue This Week ($)</label>
              <Input data-testid="backoffice-revenue-input" type="number" step="0.01" required value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} title="Total sales revenue this store generated this week." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-igc-muted text-sm mb-2 block">Orders Placed</label>
                <Input data-testid="backoffice-orders-placed-input" type="number" required value={form.orders_placed} onChange={(e) => setForm({ ...form, orders_placed: e.target.value })} title="How many orders came in this week." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto" />
              </div>
              <div>
                <label className="text-igc-muted text-sm mb-2 block">Orders Fulfilled</label>
                <Input data-testid="backoffice-orders-fulfilled-input" type="number" required value={form.orders_fulfilled} onChange={(e) => setForm({ ...form, orders_fulfilled: e.target.value })} title="How many of this week's orders were actually shipped/completed." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto" />
              </div>
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Inventory Status</label>
              <Select value={form.inventory_status} onValueChange={(v) => setForm({ ...form, inventory_status: v })}>
                <SelectTrigger data-testid="backoffice-inventory-select" title="How this store's stock levels look right now." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectItem value="Healthy">Healthy</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Monthly Profit To Date ($)</label>
              <Input data-testid="backoffice-monthly-profit-input" type="number" step="0.01" required value={form.monthly_profit_to_date} onChange={(e) => setForm({ ...form, monthly_profit_to_date: e.target.value })} title="This store's total profit so far this calendar month." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xl p-6 h-auto" />
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Performance Notes</label>
              <Textarea data-testid="backoffice-performance-notes-input" value={form.performance_notes} onChange={(e) => setForm({ ...form, performance_notes: e.target.value })} title="Internal notes about this week's performance, for admin only." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-lg p-4 min-h-24" />
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Store Updates For Client</label>
              <Textarea data-testid="backoffice-store-update-input" value={form.store_update_text} onChange={(e) => setForm({ ...form, store_update_text: e.target.value })} title="Written update the client will actually see on their dashboard once this report is approved." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-lg p-4 min-h-24" />
            </div>

            <div>
              <label className="text-igc-muted text-sm mb-2 block">Business Projections Update (optional)</label>
              <Textarea data-testid="backoffice-projections-input" value={form.projections_notes} onChange={(e) => setForm({ ...form, projections_notes: e.target.value })} title="Optional forward-looking notes about where this store's numbers are headed." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-lg p-4 min-h-24" />
            </div>

            {error && <p className="text-igc-danger text-sm" data-testid="backoffice-error">{error}</p>}

            <Button data-testid="backoffice-submit-button" type="submit" title="Submits this report for admin review — nothing goes to the client until it's approved." className="w-full h-16 text-xl bg-igc-gold hover:bg-igc-goldHover text-black">
              Submit Report
            </Button>
          </form>
        )}

        {myReports.length > 0 && (
          <div className="mt-14 border-t border-igc-gold/10 pt-8" data-testid="backoffice-my-submissions">
            <button type="button" className="flex items-center gap-2 text-igc-cream font-serif text-lg mb-4" onClick={() => setHistoryExpanded(!historyExpanded)} data-testid="backoffice-history-toggle">
              {historyExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />} My Past Submissions
            </button>
            {historyExpanded && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      data-testid={`backoffice-status-filter-${f.value}`}
                      onClick={() => setStatusFilter(f.value)}
                      title="Filter your past submissions by their review status."
                      className={`text-xs px-3 py-1.5 border transition-colors ${statusFilter === f.value ? "border-igc-gold bg-igc-gold text-black" : "border-igc-gold/30 text-igc-muted hover:text-igc-goldText"}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {myReports.filter((r) => statusFilter === "all" || r.status === statusFilter).length === 0 && (
                  <p className="text-igc-muted text-sm" data-testid="backoffice-history-empty">No submissions match this filter.</p>
                )}
                {myReports.filter((r) => statusFilter === "all" || r.status === statusFilter).map((r) => (
                  <div key={r.id} className="bg-igc-surface border border-igc-gold/15 p-4" data-testid={`backoffice-submission-${r.id}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <p className="text-igc-cream text-sm">{r.store_display_name} — week ending {r.week_ending}</p>
                      <span className={`text-[10px] px-2 py-1 tracking-wide ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`} data-testid={`backoffice-submission-status-${r.id}`}>{r.status}</span>
                    </div>
                    <p className="text-igc-muted text-xs mt-1">Submitted {formatET(r.submitted_at, "MMM d, yyyy h:mm a")}</p>
                    {r.status === "rejected" && r.rejection_note && (
                      <div className="bg-igc-danger/10 border border-igc-danger/30 p-3 mt-2" data-testid={`backoffice-rejection-note-${r.id}`}>
                        <p className="text-igc-danger text-xs uppercase tracking-widest mb-1">Sent back by Admin</p>
                        <p className="text-igc-cream text-sm">{r.rejection_note}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
